import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
  Mail, Phone, Calendar, Network, Trash2, Pencil, ShieldAlert, Send, CheckCircle2,
  Ban, NotebookPen, Sparkles, Clock, ListChecks, ChevronDown, ChevronRight, Loader2, AlertTriangle,
} from "lucide-react";
import { formatDate } from "../../../lib/date";
import { ContactsEditor } from "../ContactsEditor";
import AddToNurture, { NurtureWarmBadge } from "../AddToNurture";
import LinkedActions from "../LinkedActions";
import HubSpotHistory from "../HubSpotHistory";
import QuotesPanel from "../quotes/QuotesPanel";
import ActivityTimeline from "../ActivityTimeline";
import {
  today, STATUS_META, STATUS_ORDER, SIGNUP_PRODUCT_META, SIGNUP_LIFECYCLE_META,
  NEXT_ACTION_LABELS, ACTIVITY_TYPES, CALL_OUTCOME_LABELS, CALL_OUTCOME_ORDER,
  StatusBadge, SignupBadge,
} from "./prospectConstants";

export default function ProspectSheet({ ctx }) {
  const {
    selected, setSelected, detailTab, setDetailTab, meetings, expandedMeeting, setExpandedMeeting,
    touch, setTouch, loggingTouch, reps, API,
    markFollowUpDone, openCompose, setStatus, openConvert, setMeetingForm, setMeetingOpen, deleteMeeting,
    logTouch, setConfirmDelete, openEdit,
  } = ctx;

  return (
      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <SheetContent
          side="right"
          className="bg-[#FBFAF7] border-l border-[#DAD5CA] text-[#1A1A1A] w-full sm:max-w-[680px] shadow-drawer overflow-hidden p-0 flex flex-col"
          data-testid="sales-detail-sheet"
        >
          {selected && (
            <>
              <SheetHeader className="px-6 pt-5 pb-4 border-b border-[#DAD5CA] space-y-1 text-left bg-[#FFFFFF]">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#8A857A]">Prospect Record</span>
                <SheetTitle className="flex items-center gap-2 flex-wrap text-[#1A1A1A]">
                  <span>{selected.company_name}</span>
                  <StatusBadge status={selected.status} />
                  {selected.is_existing_client && (
                    <Badge className="bg-[#FFF7D1] text-[#6B5600] border-0 gap-1"><ShieldAlert className="w-3 h-3" /> Existing Client</Badge>
                  )}
                  <SignupBadge prospect={selected} />
                  <NurtureWarmBadge prospect={selected} testid="sales-detail-warm" />
                  {selected.needs_review && (
                    <Badge className="bg-[#FFF7D1] text-[#6B5600] border-0 gap-1" data-testid="sales-detail-review">
                      <AlertTriangle className="w-3 h-3" /> Needs review
                    </Badge>
                  )}
                </SheetTitle>
                <p className="text-xs text-[#8A857A]">
                  {[selected.priority_tier && `Tier ${selected.priority_tier}`, selected.category, selected.suburb, selected.source && `via ${selected.source}`].filter(Boolean).join(" · ")}
                </p>
              </SheetHeader>

              <Tabs value={detailTab} onValueChange={setDetailTab} className="flex-1 flex flex-col min-h-0">
                <TabsList className="mx-6 mt-4 bg-[#F4F2ED] border border-[#DAD5CA] rounded-full justify-start flex-wrap h-auto text-[12.5px]" data-testid="sales-detail-tabs">
                  <TabsTrigger value="details" data-testid="sales-tab-details" className="rounded-full">Details &amp; status</TabsTrigger>
                  <TabsTrigger value="activity" data-testid="sales-tab-activity" className="rounded-full">
                    Activity
                    <span className={`ml-1.5 mono text-[10px] px-1.5 py-0.5 rounded ${(selected.activities?.length || 0) ? "bg-[#FFF8D6] text-[#8A6D00]" : "bg-[#FBFAF7] text-[#8F8A7F] border border-[#E2DED4]"}`}>{selected.activities?.length || 0}</span>
                  </TabsTrigger>
                  <TabsTrigger value="meetings" data-testid="sales-tab-meetings" className="rounded-full">
                    Meetings
                    <span className={`ml-1.5 mono text-[10px] px-1.5 py-0.5 rounded ${meetings.length ? "bg-[#FFF8D6] text-[#8A6D00]" : "bg-[#FBFAF7] text-[#8F8A7F] border border-[#E2DED4]"}`}>{meetings.length}</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="px-6 pb-6 pt-4 space-y-4 m-0 overflow-y-auto flex-1 min-h-0">

              {selected.signup_product && (
                <div className="rounded-lg border border-[#FFF8D6] bg-[#FFF8D6] p-3 text-sm flex items-center gap-4 flex-wrap" data-testid="signup-detail">
                  <span className="text-[#8A6D00] font-medium">EQUIPR {SIGNUP_PRODUCT_META[selected.signup_product]?.label} sign-up</span>
                  {selected.signup_plan && <span className="text-[#59554D]">Plan: {selected.signup_plan}</span>}
                  <span className="text-[#59554D]">Status: {SIGNUP_LIFECYCLE_META[selected.signup_lifecycle]?.label || "Trial"}</span>
                  {selected.signup_trial_ends && selected.signup_lifecycle === "trial" && (
                    <span className="text-[#6B5600]">Trial ends {formatDate(selected.signup_trial_ends)}</span>
                  )}
                </div>
              )}

              {/* Contact + pipeline actions */}
              <div className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3 grid grid-cols-2 gap-2 text-sm">
                <div className="text-[#59554D]">{selected.contact_name || "—"}</div>
                <div className="text-[#59554D] flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {selected.contact_email || "—"}</div>
                <div className="text-[#59554D] flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {selected.contact_phone || "—"}</div>
                <div className="text-[#59554D] flex items-center gap-1">
                  <Network className="w-3.5 h-3.5" /> {selected.client_network_count ?? 0} in network
                  {selected.assigned_rep ? ` · Rep: ${selected.assigned_rep}` : ""}
                </div>
              </div>

              {selected.next_action_date && (
                <div className="rounded-lg border border-[#FFF7D1] bg-[#FFF7D1] p-3 flex items-center justify-between gap-2" data-testid="sales-next-action">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-[#6B5600]" />
                    <span className="text-[#6B5600] font-medium">Next: {NEXT_ACTION_LABELS[selected.next_action_type] || "Follow up"}</span>
                    <span className="text-[#59554D]">· {formatDate(selected.next_action_date)}</span>
                    {selected.next_action_note && <span className="text-[#8A857A] text-xs">— {selected.next_action_note}</span>}
                  </div>
                  <Button size="sm" variant="ghost" className="text-[#0E6B3F] hover:bg-[#E3F3E9] h-7"
                    onClick={() => markFollowUpDone(selected.id)} data-testid="sales-next-action-done">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Done
                  </Button>
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] font-medium"
                  onClick={() => openCompose(selected)} data-testid="sales-send-email"
                  title="Send an email">
                  <Mail className="w-4 h-4 mr-1" /> Send Email
                </Button>
                <AddToNurture prospectId={selected.id} variant="button" />
                {selected.status !== "converted" && (
                  <Button size="sm" className="bg-[#0E6B3F] hover:bg-[#0b5733] text-white font-medium"
                    onClick={() => openConvert(selected)} data-testid="sales-convert">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Convert to Client
                  </Button>
                )}
                {selected.status !== "disqualified" && (
                  <Button size="sm" variant="outline" className="border-[#DAD5CA] bg-transparent text-[#C23A00] hover:bg-[#FFE9E0]"
                    onClick={() => setStatus(selected, "disqualified")} data-testid="sales-disqualify">
                    <Ban className="w-4 h-4 mr-1" /> Disqualify
                  </Button>
                )}
                {selected.converted_client_id && (
                  <span className="text-xs text-[#0E6B3F]">→ Client {selected.converted_client_id}</span>
                )}
              </div>

              <ContactsEditor base={`${API}/sales/prospects/${selected.id}`} />

              <QuotesPanel linkType="prospect" linkId={selected.id} linkName={selected.company_name} contacts={selected.contacts || []} />

                </TabsContent>

                <TabsContent value="meetings" className="px-6 pb-6 pt-4 space-y-4 m-0 overflow-y-auto flex-1 min-h-0">
              {/* Demo / meeting notes */}
              <div className="rounded-lg border border-[#EFF6FF] bg-[#EFF6FF] p-4 space-y-3" data-testid="sales-meetings-section">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold flex items-center gap-1.5">
                    <NotebookPen className="w-3.5 h-3.5 text-[#3B82F6]" /> Demo & Meeting Notes ({meetings.length})
                  </div>
                  <Button size="sm" className="bg-[#3B82F6] hover:bg-[#EFF6FF] text-black font-medium h-8"
                    onClick={() => { setMeetingForm({ raw_notes: "", meeting_date: today(), advance_status: "demo_completed" }); setMeetingOpen(true); }}
                    data-testid="sales-add-meeting">
                    <Sparkles className="w-4 h-4 mr-1" /> Add demo notes
                  </Button>
                </div>
                {meetings.length === 0 ? (
                  <p className="text-xs text-[#8A857A]">Paste your Teams AI meeting notes and we'll auto-summarise them and create follow-ups from the action items.</p>
                ) : (
                  meetings.map((m) => (
                    <div key={m.id} className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF]" data-testid={`sales-meeting-${m.id}`}>
                      <button className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
                        onClick={() => setExpandedMeeting(expandedMeeting === m.id ? null : m.id)}
                        data-testid={`sales-meeting-toggle-${m.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm text-[#1A1A1A] font-medium truncate">{m.title}</div>
                          <div className="text-xs text-[#8A857A] flex items-center gap-2">
                            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(m.meeting_date)}</span>
                            {m.duration && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {m.duration}</span>}
                            {(m.action_items?.length > 0) && <span className="inline-flex items-center gap-1 text-[#3B82F6]"><ListChecks className="w-3 h-3" /> {m.action_items.length} action{m.action_items.length === 1 ? "" : "s"}</span>}
                          </div>
                        </div>
                        {expandedMeeting === m.id ? <ChevronDown className="w-4 h-4 text-[#8A857A] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#8A857A] shrink-0" />}
                      </button>
                      {expandedMeeting === m.id && (
                        <div className="px-3 pb-3 space-y-3 border-t border-[#F4F2ED] pt-3">
                          {m.purpose && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-[#8A857A] font-semibold mb-1">Purpose</div>
                              <p className="text-sm text-[#59554D]">{m.purpose}</p>
                            </div>
                          )}
                          {m.takeaways?.length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-[#8A857A] font-semibold mb-1">Takeaways</div>
                              <ul className="list-disc pl-4 space-y-0.5 text-sm text-[#59554D]">
                                {m.takeaways.map((t, i) => <li key={i}>{t}</li>)}
                              </ul>
                            </div>
                          )}
                          {m.summary_sections?.map((s, i) => (
                            <div key={i}>
                              <div className="text-[10px] uppercase tracking-wider text-[#8A857A] font-semibold mb-1">{s.heading}</div>
                              <ul className="list-disc pl-4 space-y-0.5 text-sm text-[#59554D]">
                                {(s.points || []).map((p, j) => <li key={j}>{p}</li>)}
                              </ul>
                            </div>
                          ))}
                          {m.action_items?.length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-[#8A857A] font-semibold mb-1">Action items</div>
                              <div className="space-y-1">
                                {m.action_items.map((a, i) => (
                                  <div key={i} className="flex items-start gap-2 text-sm">
                                    <ListChecks className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#8A857A]" />
                                    <span className="text-[#59554D]">{a.text}</span>
                                    {a.owner && (
                                      <Badge className={`border-0 shrink-0 ${a.is_rep ? "bg-[#EFF6FF] text-[#3B82F6]" : "bg-[#DAD5CA] text-[#59554D]"}`}>
                                        {a.owner}{a.is_rep ? " · us" : ""}
                                      </Badge>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="flex justify-end">
                            <Button size="sm" variant="ghost" className="h-7 text-[#C23A00] hover:bg-[#FFE9E0]"
                              onClick={() => deleteMeeting(m.id)} data-testid={`sales-meeting-delete-${m.id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

                </TabsContent>

                <TabsContent value="activity" className="px-6 pb-6 pt-4 space-y-4 m-0 overflow-y-auto flex-1 min-h-0">
              {/* Log a touch */}
              <div className="rounded-lg border border-[#FFF8D6] bg-[#FFF8D6] p-4 space-y-3" data-testid="sales-touch-form">
                <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Log a Touch</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#59554D]">Type</label>
                    <Select value={touch.type} onValueChange={(v) => setTouch({ ...touch, type: v })}>
                      <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-touch-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                        {ACTIVITY_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[#59554D]">Date</label>
                    <Input type="date" value={touch.activity_date} onChange={(e) => setTouch({ ...touch, activity_date: e.target.value })}
                      className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-touch-date" />
                  </div>
                  <div>
                    <label className="text-xs text-[#59554D]">Rep</label>
                    <Select value={touch.rep || "unassigned"} onValueChange={(v) => setTouch({ ...touch, rep: v === "unassigned" ? "" : v })}>
                      <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-touch-rep"><SelectValue placeholder="Rep" /></SelectTrigger>
                      <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                        <SelectItem value="unassigned">—</SelectItem>
                        {reps.map(a => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[#59554D]">Advance status to</label>
                    <Select value={touch.advance_status} onValueChange={(v) => setTouch({ ...touch, advance_status: v })}>
                      <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-touch-advance"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                        <SelectItem value="none">Leave unchanged</SelectItem>
                        {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {touch.type === "call" && (
                  <div className="grid grid-cols-2 gap-3 rounded-lg border border-[#1D4ED8] bg-[#1D4ED8] p-3">
                    <div>
                      <label className="text-xs text-[#59554D]">Call outcome (auto-schedules next action)</label>
                      <Select value={touch.call_outcome} onValueChange={(v) => setTouch({ ...touch, call_outcome: v })}>
                        <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-call-outcome"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                          <SelectItem value="none">— No auto-action —</SelectItem>
                          {CALL_OUTCOME_ORDER.map(c => <SelectItem key={c} value={c}>{CALL_OUTCOME_LABELS[c]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {touch.call_outcome === "callback_requested" && (
                      <div>
                        <label className="text-xs text-[#59554D]">Callback date</label>
                        <Input type="date" value={touch.callback_date} onChange={(e) => setTouch({ ...touch, callback_date: e.target.value })}
                          className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-callback-date" />
                      </div>
                    )}
                  </div>
                )}
                <Input value={touch.outcome} onChange={(e) => setTouch({ ...touch, outcome: e.target.value })}
                  placeholder="Outcome (e.g. left voicemail, positive reply)" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="sales-touch-outcome" />
                <Textarea value={touch.notes} onChange={(e) => setTouch({ ...touch, notes: e.target.value })}
                  placeholder="Notes" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] min-h-[50px]" data-testid="sales-touch-notes" />
                <div className="flex justify-end">
                  <Button size="sm" className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]" onClick={logTouch} disabled={loggingTouch} data-testid="sales-touch-save">
                    {loggingTouch && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Log Touch
                  </Button>
                </div>
              </div>

              {/* Management actions linked to this prospect */}
              <LinkedActions linkType="prospect" linkId={selected.id} />

              {/* Unified activity timeline (native touches + imported HubSpot history) */}
              <ActivityTimeline
                key={`pt-${selected.id}-${selected.activities?.length || 0}`}
                entity="prospect"
                id={selected.id}
                onCompleteTask={async () => { await markFollowUpDone(selected.id); }}
              />
                </TabsContent>
              </Tabs>

              {/* Pinned footer (View/Edit split) */}
              <div className="flex items-center justify-between gap-2 px-6 py-3 border-t border-[#DAD5CA] bg-[#FFFFFF]">
                <Button variant="ghost" onClick={() => setConfirmDelete(selected)}
                  className="text-[#C23A00] hover:bg-[#FFE9E0] hover:text-[#C23A00]" data-testid="sales-remove">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Button variant="outline" onClick={() => openEdit(selected)}
                  className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="sales-detail-edit">
                  <Pencil className="w-4 h-4 mr-1" /> Edit prospect
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
  );
}
